import { HttpErrorResponse } from '@angular/common/http';

import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { VideoReviewState } from '@common/modules/shared/interfaces';

import * as MediaActions from '../actions/media.actions';

import PlaylistContractV2 = Microsoft.VideoIndexer.Contracts.PlaylistContractV2;
import VideoContract = Microsoft.VideoIndexer.Contracts.VideoContract;

export const mediaFeatureKey = 'media';

export interface IState extends EntityState<PlaylistContractV2> {
  selectedIndexId: string;
  saving: boolean;
  error: HttpErrorResponse;
  insightsFilterValue: string;
}

export function selectIndexId(a: PlaylistContractV2): string {
  return a.id;
}

export const adapter: EntityAdapter<PlaylistContractV2> = createEntityAdapter<PlaylistContractV2>({
  selectId: selectIndexId
});

export const initialState: IState = adapter.getInitialState({
  // additional entity state properties
  selectedIndexId: null,
  saving: false,
  error: null,
  insightsFilterValue: ''
});

function updateFirstVideo(state: IState, id: string, updater: (video: VideoContract) => VideoContract): IState {
  const entity = state.entities[id];
  const hasVideos = entity?.videos && entity.videos.length > 0;

  if (!hasVideos) {
    return state;
  }

  const [firstVideo, ...restVideos] = entity.videos;
  const updatedFirstVideo = updater(firstVideo);

  if (!updatedFirstVideo) {
    return state;
  }

  return adapter.updateOne(
    {
      id,
      changes: {
        videos: [updatedFirstVideo, ...restVideos]
      }
    },
    state
  );
}

const mediaReducer = createReducer(
  initialState,
  on(MediaActions.upsertMediaIndex, (state, { mediaIndex }) => {
    return adapter.upsertOne(mediaIndex, state);
  }),
  on(MediaActions.updateIndex, (state, { mediaIndex }) => {
    return adapter.updateOne(mediaIndex, { ...state, saving: true });
  }),
  on(MediaActions.updateIndexSaving, (state, { saving }) => {
    return {
      ...state,
      saving: saving
    };
  }),
  on(MediaActions.selectMediaIndex, (state, { id }) => {
    return {
      ...state,
      selectedIndexId: id,
      insightsFilterValue: ''
    };
  }),
  on(MediaActions.loadMediaFailure, (state, { error }) => {
    return {
      ...state,
      error: error
    };
  }),
  on(MediaActions.loadVideoIndex, state => {
    return {
      ...state,
      error: null
    };
  }),
  on(MediaActions.loadProjectIndex, state => {
    return {
      ...state,
      error: null
    };
  }),
  on(MediaActions.requestReview, (state, { videoId }) => {
    return updateFirstVideo(state, videoId, video => ({
      ...video,
      reviewState: VideoReviewState.IN_PROGRESS
    }));
  }),
  on(MediaActions.updateMediaReviewState, (state, { videoId, reviewState }) => {
    return updateFirstVideo(state, videoId, video => ({
      ...video,
      reviewState
    }));
  }),
  on(MediaActions.removeMediaIndex, (state, { id }) => {
    return adapter.removeOne(id, state);
  }),
  on(MediaActions.updateThumbnailId, (state, { id, thumbnailId }) => {
    return updateFirstVideo(state, id, video => ({
      ...video,
      thumbnailId
    }));
  }),
  on(MediaActions.updateInsights, (state, { id, data }) => {
    return updateFirstVideo(state, id, video => ({
      ...video,
      insights: {
        ...video.insights,
        [data.type as string]: data.insights
      }
    }));
  }),
  on(MediaActions.updateFace, (state, { id, face }) => {
    return updateFirstVideo(state, id, video => {
      const faces = video.insights?.faces || [];
      const index = faces.findIndex(faceElement => faceElement.id === face.id);

      if (index === -1) {
        return null;
      }

      const updatedFaces = [...faces];
      updatedFaces[index] = face;

      return {
        ...video,
        insights: {
          ...video.insights,
          faces: updatedFaces
        }
      };
    });
  }),
  on(MediaActions.updateTranscriptLine, (state, { id, line }) => {
    return updateFirstVideo(state, id, video => {
      const transcript = video.insights?.transcript || [];
      const index = transcript.findIndex(transcriptElement => transcriptElement.id === line.id);

      if (index === -1) {
        return null;
      }

      const updatedTranscript = [...transcript];
      updatedTranscript[index] = { ...line };

      return {
        ...video,
        insights: {
          ...video.insights,
          transcript: updatedTranscript
        }
      };
    });
  }),
  on(MediaActions.updateOcrLine, (state, { id, line }) => {
    return updateFirstVideo(state, id, video => {
      const ocr = video.insights?.ocr || [];
      const index = ocr.findIndex(ocrElement => ocrElement.id === line.id);

      if (index === -1) {
        return null;
      }

      const updatedOcr = [...ocr];
      updatedOcr[index] = { ...line };

      return {
        ...video,
        insights: {
          ...video.insights,
          ocr: updatedOcr
        }
      };
    });
  }),
  on(MediaActions.updateAllTranscriptLines, (state, { id, transcript }) => {
    return updateFirstVideo(state, id, video => ({
      ...video,
      insights: {
        ...video.insights,
        transcript
      }
    }));
  }),
  on(MediaActions.setInsightsFilterValue, (state, { value }) => {
    return {
      ...state,
      insightsFilterValue: value
    };
  })
);

export function reducer(state: IState | undefined, action: Action) {
  return mediaReducer(state, action);
}

// get the selectors
const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

// select the array of user ids
export const selectIndexIds = selectIds;

// select the dictionary of user entities
export const selectIndexEntities = selectEntities;

// select the array of users
export const selectAllIndexes = selectAll;

// select the total user count
export const selectIndexTotal = selectTotal;
